import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import { Typography } from '~/components/common/Typography'
import { TaskStatuses } from '~/constants/task-status'
import { AgentTaskStatus } from '~/protos/agents'
import { cm } from '~/utils'

export type LandingTaskStatusGroup =
  | 'IN_PROGRESS'
  | 'NEED_ACTION'
  | 'COMPLETED'
  | 'ERRORED'
  | 'STOPPED'
  | string
  | undefined

export interface LandingTaskPanelTab<T extends string> {
  disabled?: boolean
  icon: ReactNode
  id: T
  label: string
}

interface LandingTaskPanelFrameProps {
  children: ReactNode
  className?: string
}

interface LandingTaskPanelHeaderProps {
  actions?: ReactNode
  alert?: ReactNode
  meta?: ReactNode
  onClose?: () => void
  subtitle?: ReactNode
  title: ReactNode
}

interface LandingTaskPanelTabsProps<T extends string> {
  activeTab: T
  onChange: (tab: T) => void
  tabs: Array<LandingTaskPanelTab<T>>
}

interface LandingTaskPanelFooterProps {
  children: ReactNode
  className?: string
}

interface LandingTaskPanelNoticeProps {
  children: ReactNode
  icon?: ReactNode
  tone?: 'info' | 'error' | 'success' | 'neutral'
}

interface LandingTaskStatusPillProps {
  group?: LandingTaskStatusGroup
  label: string
  status?: AgentTaskStatus
}

export function landingTaskStatusGroupClass(group?: LandingTaskStatusGroup): string {
  if (group === 'NEED_ACTION') {
    return 'border-tokens-status-link-2/20 bg-tokens-status-link-base-2 text-tokens-status-link-3'
  }
  if (group === 'COMPLETED') {
    return 'border-tokens-status-success-2 bg-tokens-status-success-3 text-tokens-status-success-1'
  }
  if (group === 'ERRORED') {
    return 'border-tokens-status-error-30 bg-tokens-status-error-10 text-tokens-status-error-text'
  }
  if (group === 'STOPPED') {
    return 'border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 text-tokens-neutral-text-secondary'
  }
  return 'border-tokens-status-link-2/20 bg-tokens-status-link-base text-tokens-status-link-3'
}

export function landingTaskStatusClass(status: AgentTaskStatus): string {
  return landingTaskStatusGroupClass(TaskStatuses[status]?.group)
}

export function LandingTaskStatusPill({ group, label, status }: LandingTaskStatusPillProps) {
  const statusGroup = group ?? (status !== undefined ? TaskStatuses[status]?.group : undefined)

  return (
    <div
      className={cm(
        'shrink-0 rounded-full border px-3 py-1 text-xs font-semibold',
        landingTaskStatusGroupClass(statusGroup)
      )}
    >
      {label}
    </div>
  )
}

export function LandingTaskPanelFrame({ children, className }: LandingTaskPanelFrameProps) {
  return (
    <div
      className={cm(
        'flex h-full min-h-0 w-full flex-col overflow-hidden rounded-[28px] border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white shadow-segment',
        className
      )}
    >
      {children}
    </div>
  )
}

export function LandingTaskPanelHeader({
  actions,
  alert,
  meta,
  onClose,
  subtitle,
  title
}: LandingTaskPanelHeaderProps) {
  return (
    <div className='flex min-h-[68px] shrink-0 flex-col justify-center border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-white px-2 py-4'>
      <div className='flex min-h-9 items-center justify-between gap-4'>
        <div className='min-w-0 flex-1'>
          {typeof title === 'string' ? (
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              fontWeight='medium'
              className='truncate text-tokens-neutral-text-primary'
            >
              {title}
            </Typography>
          ) : (
            title
          )}
          {subtitle && (
            <Typography
              fontFamily='geist'
              fontSize='bodySm'
              className='mt-1 truncate text-tokens-neutral-text-secondary'
            >
              {subtitle}
            </Typography>
          )}
          {meta && (
            <div className='mt-1 flex flex-wrap items-center gap-2 text-xs text-tokens-neutral-text-secondary'>
              {meta}
            </div>
          )}
        </div>
        {(actions || onClose) && (
          <div className='flex shrink-0 items-center justify-end gap-2'>
            {actions}
            {onClose && (
              <button
                type='button'
                onClick={onClose}
                className='flex size-9 shrink-0 items-center justify-center rounded-[12px] text-tokens-neutral-icon-hover transition-colors hover:bg-tokens-neutral-fill-fill-4'
                aria-label='Close task side panel'
                title='Close task panel'
              >
                <X className='size-4' />
              </button>
            )}
          </div>
        )}
      </div>
      {alert}
    </div>
  )
}

export function LandingTaskPanelTabs<T extends string>({
  activeTab,
  onChange,
  tabs
}: LandingTaskPanelTabsProps<T>) {
  return (
    <div className='shrink-0 border-b border-tokens-neutral-border-2 px-4 py-3'>
      <div
        className='grid gap-1 rounded-2xl bg-tokens-neutral-bg-grey-2 p-1'
        style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
      >
        {tabs.map(tab => (
          <button
            key={tab.id}
            type='button'
            disabled={tab.disabled}
            onClick={() => onChange(tab.id)}
            className={cm(
              'flex h-10 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors',
              activeTab === tab.id
                ? 'bg-tokens-neutral-bg-white text-tokens-neutral-text-primary shadow-segment'
                : 'text-tokens-neutral-text-secondary hover:bg-tokens-neutral-fill-fill-4 hover:text-tokens-neutral-text-primary disabled:text-tokens-neutral-text-quaternary disabled:hover:bg-transparent'
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export function LandingTaskPanelBody({ children, className }: LandingTaskPanelFrameProps) {
  return <div className={cm('min-h-0 flex-1 overflow-auto', className)}>{children}</div>
}

export function LandingTaskPanelFooter({ children, className }: LandingTaskPanelFooterProps) {
  return (
    <div className={cm('shrink-0 border-t border-tokens-neutral-border-2 p-4', className)}>
      {children}
    </div>
  )
}

export function LandingTaskPanelNotice({
  children,
  icon,
  tone = 'info'
}: LandingTaskPanelNoticeProps) {
  const toneClassName =
    tone === 'error'
      ? 'border-tokens-status-error-30 bg-tokens-status-error-10 text-tokens-status-error-text'
      : tone === 'success'
        ? 'border-tokens-status-success-2 bg-tokens-status-success-3 text-tokens-status-success-1'
        : tone === 'neutral'
          ? 'border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-2 text-tokens-neutral-text-secondary'
          : 'border-tokens-status-link-2/20 bg-tokens-status-link-base-2 text-tokens-status-link-3'

  return (
    <div
      className={cm('flex items-center gap-2 rounded-xl border px-3 py-2 text-sm', toneClassName)}
    >
      {icon}
      <span>{children}</span>
    </div>
  )
}
